import React, { useState } from 'react'

import defaultImage from 'assets/svg/default-image-square.svg'
import clsx from 'clsx'

export interface ThumbnailProps {
  /**
   * Is this the principal call to action on the page?
   */
  variants?: 'primary' | 'secondary'
  /**
   * Optional classname
   */
  className?: string
  /**
   * Source of image
   */
  src?: string
  alt?: string
  /**
   * Optional click handler
   */
  onClick?: () => void
  onError?: () => void
}

/**
 * Thumbnail for display image
 */
export const Thumbnail: React.FC<ThumbnailProps> = ({
  variants = 'primary',
  className,
  src = defaultImage,
  alt = 'thumbnail-img',
  onClick = () => {},
  onError = () => {},
  ...others
}: ThumbnailProps): JSX.Element => {
  const [isError, setIsError] = useState(false)
  const base = 'thumbnail'
  const isDefault = src === defaultImage
  const variantsCn = clsx(
    `${base}--${variants}`,
    (isDefault || isError) && `${base}--default-image`,
  )
  const cn = clsx(base, variantsCn, className)

  const thumbnailProps = {
    ...others,
    src: src || defaultImage,
    alt,
    className: cn,
    onClick: !isError ? onClick : () => {},
    onError,
  }

  const handleError = (
    currentTarget: EventTarget & HTMLImageElement,
    onError?: () => void,
  ) => {
    setIsError(true)

    currentTarget.onerror = null
    currentTarget.src = defaultImage
    currentTarget.className = `${base}--default-image ${currentTarget.className}`

    if (onError) {
      onError()
    }
  }

  return (
    <img
      {...thumbnailProps}
      onError={({ currentTarget }) => handleError(currentTarget, onError)}
    />
  )
}

export default Thumbnail
